<template>
  <div class="signin">
    <!-- Better Auth sign-in (plan Task 11). Colours come only from the tenant
         theme (`color="primary"`, the --sh-* tokens), so each tenant draws it in
         its own brand. The parent exchanges the session for a bridge token. -->
    <div v-if="step === 'two-factor'" class="signin__body">
      <form class="signin__form" data-test="two-factor-form" @submit.prevent="verifyCode">
        <p class="signin__title">{{ t("auth.identity.twoFactorTitle") }}</p>
        <p class="signin__hint">{{ useBackupCode ? t("auth.identity.backupHint") : t("auth.identity.twoFactorHint") }}</p>
        <v-text-field
          v-model="code"
          :label="t('auth.identity.code')"
          variant="outlined"
          density="comfortable"
          :autocomplete="useBackupCode ? 'off' : 'one-time-code'"
          :inputmode="useBackupCode ? 'text' : 'numeric'"
          autofocus
          :disabled="!!busy"
          hide-details
        />
        <v-btn class="signin__submit" type="submit" color="primary" size="large" block :loading="busy === 'code'">
          {{ t("auth.identity.verify") }}
        </v-btn>
        <v-btn class="signin__link" variant="text" size="small" block :disabled="!!busy" @click="toggleBackupCode">
          {{ useBackupCode ? t("auth.identity.useAuthenticator") : t("auth.identity.useBackupCode") }}
        </v-btn>
      </form>
    </div>

    <div v-else-if="step === 'forgot'" class="signin__body">
      <form class="signin__form" data-test="forgot-form" @submit.prevent="sendResetLink">
        <p class="signin__hint">{{ t("auth.identity.forgotIntro") }}</p>
        <v-text-field
          v-model="email"
          :label="t('auth.identity.email')"
          type="email"
          variant="outlined"
          density="comfortable"
          autocomplete="email"
          autofocus
          :disabled="!!busy"
          hide-details
        />
        <v-btn class="signin__submit" type="submit" color="primary" size="large" block :loading="busy === 'reset'">
          {{ t("auth.identity.sendResetLink") }}
        </v-btn>
      </form>
    </div>

    <!-- Plain elements, not <template> fragments: a switch between fragment
         branches broke the DOM patch (Vue "removeFragment", seen in tests). -->
    <div v-else class="signin__body">
      <div v-if="socialProviders.length" class="signin__social">
        <v-btn
          v-for="provider in socialProviders"
          :key="provider"
          :data-test="`provider-${provider}`"
          variant="outlined"
          size="large"
          block
          :loading="busy === provider"
          :disabled="!!busy && busy !== provider"
          @click="startSocial(provider)"
        >
          {{ provider === "google" ? t("auth.identity.continueWithGoogle") : t("auth.identity.continueWithMicrosoft") }}
        </v-btn>
      </div>

      <p v-if="socialProviders.length && (showPassword || showMagicLink)" class="signin__or">
        <span>{{ t("auth.identity.or") }}</span>
      </p>

      <form v-if="showPassword" class="signin__form" data-test="password-form" @submit.prevent="submitPassword">
        <v-text-field
          v-if="step === 'sign-up'"
          v-model="name"
          :label="t('auth.identity.name')"
          variant="outlined"
          density="comfortable"
          autocomplete="name"
          :disabled="!!busy"
          hide-details
        />
        <v-text-field
          v-model="email"
          :label="t('auth.identity.email')"
          type="email"
          variant="outlined"
          density="comfortable"
          autocomplete="email"
          :disabled="!!busy"
          hide-details
        />
        <v-text-field
          v-model="password"
          :label="t('auth.identity.password')"
          type="password"
          variant="outlined"
          density="comfortable"
          :autocomplete="step === 'sign-up' ? 'new-password' : 'current-password'"
          :disabled="!!busy"
          hide-details
        />
        <v-btn class="signin__submit" type="submit" color="primary" size="large" block :loading="busy === 'password'">
          {{ step === "sign-up" ? t("auth.identity.signUp") : submitLabel }}
        </v-btn>
      </form>

      <form v-else-if="showMagicLink" class="signin__form" data-test="magic-form" @submit.prevent="sendMagicLink">
        <v-text-field
          v-model="email"
          :label="t('auth.identity.email')"
          type="email"
          variant="outlined"
          density="comfortable"
          autocomplete="email"
          :disabled="!!busy"
          hide-details
        />
      </form>

      <v-btn
        v-if="showMagicLink && step === 'sign-in'"
        data-test="provider-magicLink"
        :class="{ signin__link: showPassword }"
        :variant="showPassword ? 'text' : 'flat'"
        :color="showPassword ? undefined : 'primary'"
        :size="showPassword ? 'small' : 'large'"
        block
        :loading="busy === 'magic'"
        :disabled="!!busy && busy !== 'magic'"
        @click="sendMagicLink"
      >
        {{ t("auth.identity.sendLink") }}
      </v-btn>

      <v-btn
        v-if="showPassword && step === 'sign-in'"
        data-test="forgot-password"
        class="signin__link"
        variant="text"
        size="small"
        block
        :disabled="!!busy"
        @click="openStep('forgot')"
      >
        {{ t("auth.identity.forgotPassword") }}
      </v-btn>

      <p v-if="allowSignUp && showPassword" class="signin__switch">
        {{ step === "sign-up" ? t("auth.identity.haveAccount") : t("auth.identity.noAccount") }}
        <a href="#" @click.prevent="openStep(step === 'sign-up' ? 'sign-in' : 'sign-up')">
          {{ step === "sign-up" ? submitLabel : t("auth.identity.createAccount") }}
        </a>
      </p>
    </div>

    <p v-if="notice" class="signin__notice" role="status" data-test="notice">
      <v-icon size="15">info_outline</v-icon>
      <span>{{ notice }}</span>
    </p>
    <v-btn
      v-if="needsVerification"
      data-test="resend-verification"
      variant="outlined"
      block
      :loading="busy === 'resend'"
      @click="resendVerification"
    >
      {{ t("auth.identity.resendLink") }}
    </v-btn>
    <p v-if="errorText" class="sh-card__error signin__error" role="alert">
      <v-icon size="15">error_outline</v-icon>
      <span>{{ errorText }}</span>
    </p>

    <v-btn v-if="step !== 'sign-in'" class="signin__link" variant="text" size="small" block :disabled="!!busy" @click="openStep('sign-in')">
      {{ t("auth.identity.back") }}
    </v-btn>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import {
  ALL_IDENTITY_PROVIDERS,
  authClient,
  identityReturnUrl,
  resetPasswordUrl,
  sendVerificationLink,
  signInWithProvider,
  SOCIAL_PROVIDERS,
  toIdentityError,
  type IdentityError,
  type IdentityProvider,
  type SocialProvider,
} from "@/auth/identity";
import { humaniseIdentityError } from "@/auth/authErrors";

type Step = "sign-in" | "sign-up" | "forgot" | "two-factor";
type Busy = "password" | "magic" | "reset" | "resend" | "code" | SocialProvider;

export default defineComponent({
  name: "SignInForm",
  props: {
    /** The methods this tenant shows (`loginPage.providers`). */
    providers: {
      type: Array as PropType<IdentityProvider[]>,
      default: () => [...ALL_IDENTITY_PROVIDERS],
    },
    /** The password button text; empty means the default "Sign in". */
    signInLabel: { type: String, default: "" },
    /** Show "Create an account" (apps that take self-serve sign-ups). */
    allowSignUp: { type: Boolean, default: false },
  },
  emits: {
    "signed-in": () => true,
    error: (payload: { code: string; message: string }) => !!payload,
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const step = ref<Step>("sign-in");
    const email = ref("");
    const password = ref("");
    const name = ref("");
    const code = ref("");
    const useBackupCode = ref(false);
    const busy = ref<Busy | null>(null);
    const notice = ref<string | null>(null);
    const errorText = ref<string | null>(null);
    const needsVerification = ref(false);

    const showPassword = computed(() => props.providers.includes("password"));
    const showMagicLink = computed(() => props.providers.includes("magicLink"));
    const socialProviders = computed(() => SOCIAL_PROVIDERS.filter((p) => props.providers.includes(p)));
    const submitLabel = computed(() => props.signInLabel.trim() || t("auth.signIn"));

    function clearMessages(): void {
      notice.value = null;
      errorText.value = null;
      needsVerification.value = false;
    }

    function openStep(next: Step): void {
      clearMessages();
      step.value = next;
      code.value = "";
      useBackupCode.value = false;
    }

    function showError(error: IdentityError): void {
      if (error.code === "EMAIL_NOT_VERIFIED") {
        needsVerification.value = true;
        notice.value = t("auth.identity.unverified");
      } else {
        errorText.value = humaniseIdentityError(error);
      }
      emit("error", { code: error.code, message: error.message });
    }

    /** One guard for every action: clear old messages, check the email, mark the button busy. */
    async function run(kind: Busy, needsEmail: boolean, action: () => Promise<void>): Promise<void> {
      if (busy.value) return;
      clearMessages();
      if (needsEmail && !email.value.trim()) {
        errorText.value = t("auth.identity.emailRequired");
        return;
      }
      busy.value = kind;
      try {
        await action();
      } catch (e) {
        showError({ code: "NETWORK", message: e instanceof Error ? e.message : String(e), status: 0 });
      } finally {
        busy.value = null;
      }
    }

    const submitPassword = () =>
      run("password", true, async () => {
        if (!password.value) {
          errorText.value = t("auth.identity.passwordRequired");
          return;
        }
        const address = email.value.trim();
        if (step.value === "sign-up") {
          const { error } = await authClient.signUp.email({
            name: name.value.trim() || address,
            email: address,
            password: password.value,
            callbackURL: identityReturnUrl(),
          });
          if (error) return showError(toIdentityError(error));
          step.value = "sign-in";
          password.value = "";
          notice.value = t("auth.identity.signUpSent", { email: address });
          return;
        }
        const { data, error } = await authClient.signIn.email({ email: address, password: password.value });
        if (error) return showError(toIdentityError(error));
        if ((data as { twoFactorRedirect?: boolean } | null)?.twoFactorRedirect) {
          openStep("two-factor");
          return;
        }
        emit("signed-in");
      });

    const sendMagicLink = () =>
      run("magic", true, async () => {
        const address = email.value.trim();
        const { error } = await authClient.signIn.magicLink({ email: address, callbackURL: identityReturnUrl() });
        if (error) return showError(toIdentityError(error));
        notice.value = t("auth.identity.linkSent", { email: address });
      });

    const sendResetLink = () =>
      run("reset", true, async () => {
        const address = email.value.trim();
        const { error } = await authClient.requestPasswordReset({ email: address, redirectTo: resetPasswordUrl() });
        if (error) return showError(toIdentityError(error));
        step.value = "sign-in";
        notice.value = t("auth.identity.resetSent", { email: address });
      });

    const resendVerification = () =>
      run("resend", true, async () => {
        const address = email.value.trim();
        const error = await sendVerificationLink(address);
        if (error) return showError(error);
        notice.value = t("auth.identity.verificationSent", { email: address });
      });

    const startSocial = (provider: SocialProvider) =>
      run(provider, false, async () => {
        const error = await signInWithProvider(provider, `${window.location.pathname}${window.location.search}`);
        if (error) showError(error);
      });

    const verifyCode = () =>
      run("code", false, async () => {
        const value = code.value.replace(/\s+/g, "");
        if (!value) {
          errorText.value = humaniseIdentityError({ code: "INVALID_CODE" });
          return;
        }
        const { error } = useBackupCode.value
          ? await authClient.twoFactor.verifyBackupCode({ code: value })
          : await authClient.twoFactor.verifyTotp({ code: value });
        if (error) return showError(toIdentityError(error));
        emit("signed-in");
      });

    function toggleBackupCode(): void {
      clearMessages();
      code.value = "";
      useBackupCode.value = !useBackupCode.value;
    }

    return {
      t,
      step,
      email,
      password,
      name,
      code,
      useBackupCode,
      busy,
      notice,
      errorText,
      needsVerification,
      showPassword,
      showMagicLink,
      socialProviders,
      submitLabel,
      openStep,
      submitPassword,
      sendMagicLink,
      sendResetLink,
      resendVerification,
      startSocial,
      verifyCode,
      toggleBackupCode,
    };
  },
});
</script>

<style scoped>
.signin {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.signin__body,
.signin__social,
.signin__form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.signin__submit {
  font-weight: 600;
}
.signin__or {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 2px 0;
  font-size: 12px;
  color: var(--sh-text-60);
}
.signin__or::before,
.signin__or::after {
  content: "";
  flex: 1;
  border-top: 1px solid var(--sh-hairline);
}
.signin__title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--sh-text);
}
.signin__hint {
  margin: 0;
  font-size: 12.5px;
  color: var(--sh-text-60);
}
.signin__link {
  font-size: 12px;
  color: var(--sh-text-60);
}
.signin__notice {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 0;
  font-size: 13px;
  line-height: 1.4;
  color: var(--sh-text-60);
}
.signin__error {
  margin: 0;
}
.signin__switch {
  margin: 4px 0 0;
  text-align: center;
  font-size: 12.5px;
  color: var(--sh-text-60);
}
.signin__switch a {
  color: rgb(var(--v-theme-primary));
  font-weight: 600;
  text-decoration: none;
}
</style>
